import { useState } from 'react';
import { Sidebar, roleMenus } from './Sidebar';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Menu, Bell, LogOut, Settings, User, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';

export default function AppLayout({ role, children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const { notifications, dismissNotification, logout } = useApp();
  const menu = roleMenus[role];

  const unreadCount = notifications.length;

  return (
    <div className="min-h-screen h-screen bg-background flex overflow-hidden">
      <Sidebar
        role={role}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(prev => !prev)}
        onLogout={logout}
      />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header
          data-testid="app-topbar"
          className="sticky top-0 z-30 bg-card border-b border-border shrink-0"
        >
          <div className="flex items-center justify-between px-4 md:px-6 h-16">
            {/* Left: Mobile menu + Institutional logo */}
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-muted-foreground h-9 w-9"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>

              <div className="flex items-center gap-3">
                <img
                  src="/regione-lazio.png"
                  alt="Regione Lazio"
                  className="h-9 w-auto object-contain"
                />
                <div className="hidden md:block border-l border-border pl-3">
                  <p className="text-sm font-semibold text-foreground leading-tight">
                    Regione Lazio
                  </p>
                  <p className="text-[10px] text-muted-foreground leading-tight">
                    Assistenza Domiciliare Integrata
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Notifications + Profile */}
            <div className="flex items-center gap-2">
              {/* Notification Center */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative h-9 w-9 text-muted-foreground hover:text-foreground"
                    data-testid="notification-bell-btn"
                  >
                    <Bell className="w-[18px] h-[18px]" />
                    {unreadCount > 0 && (
                      <span
                        data-testid="notification-badge"
                        className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-destructive rounded-full"
                      >
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  className="w-80 p-0"
                  data-testid="notification-panel"
                >
                  <div className="px-4 py-3 border-b border-border">
                    <h4 className="text-sm font-semibold text-foreground">Notifiche</h4>
                    <p className="text-xs text-muted-foreground">
                      {unreadCount > 0 ? `${unreadCount} nuove notifiche` : 'Nessuna nuova notifica'}
                    </p>
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <Bell className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Tutto in ordine!</p>
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <div
                          key={notif.id}
                          data-testid={`notification-item-${notif.id}`}
                          className="px-4 py-3 border-b border-border last:border-0 hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground">{notif.messaggio}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{notif.timestamp}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-foreground shrink-0"
                              onClick={() => dismissNotification(notif.id)}
                              data-testid={`dismiss-notif-${notif.id}`}
                            >
                              <span className="text-xs">&times;</span>
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Separator */}
              <div className="w-px h-6 bg-border mx-1 hidden sm:block" />

              {/* User Profile Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-profile-btn"
                    className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-muted transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                        OP
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden sm:block text-left">
                      <p className="text-sm font-medium text-foreground leading-tight">Operatore ADI</p>
                      <p className="text-[10px] text-muted-foreground leading-tight">{menu?.shortTitle}</p>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" data-testid="user-dropdown-menu">
                  <DropdownMenuLabel>
                    <div>
                      <p className="text-sm font-medium">Operatore ADI</p>
                      <p className="text-xs text-muted-foreground font-normal">{menu?.title}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="dropdown-profile-btn"
                    className="gap-2 cursor-pointer"
                  >
                    <User className="w-4 h-4" />
                    <span>Profilo</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="dropdown-settings-btn"
                    className="gap-2 cursor-pointer"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Impostazioni</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="dropdown-change-role-btn"
                    className="gap-2 cursor-pointer"
                    onClick={() => navigate('/')}
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Cambia Ruolo</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="dropdown-logout-btn"
                    className="gap-2 cursor-pointer text-destructive focus:text-destructive"
                    onClick={() => { logout(); navigate('/'); }}
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Esci</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto" data-testid="app-main-content">
          <div className="px-4 md:px-6 lg:px-8 py-6 lg:py-8 max-w-[1400px]">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
